import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import clsx from "clsx";
import Typography from "@material-ui/core/Typography";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import DescriptionIcon from "@material-ui/icons/Description";
import UploadIcon from "@material-ui/icons/CloudUpload";
import GetAppIcon from "@material-ui/icons/GetApp";
import A_Button from "../../../Atoms/Inputs/Buttons/A_Button";
import M_DataStateRenderer from "../../../Molecules/Common/DataStateRenderer/M_DataStateRenderer.jsx";
import LoaderSkeleton from "../../../Molecules/Common/LoaderSkeleton";
import StatusTag from "./StatusTag";
import { api, fetchBankOptionSets } from "./ProfessionalContacts.helper";
import {
  buildBulkUploadSummary,
  downloadBulkUploadTemplate,
  parseBulkUploadWorkbook,
} from "./BulkUpload.workbook";
import useStyles from "./ProfessionalContacts.styles";
import A_SecuredAction from "../../../Atoms/Security/A_SecuredAction";
import { pageSecurityActions } from "../../../../../Utils/Helpers/pageSecurity";

const STATUS_TONE = { Ready: "valid", Warning: "expiring", Error: "danger" };

const XLSX_MIME =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

const BulkUpload = ({ onBack }) => {
  const classes = useStyles();
  const fileInputRef = useRef(null);

  const [optionSets, setOptionSets] = useState({ bankType: [], country: [] });
  const [file, setFile] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [summary, setSummary] = useState(null);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [error, setError] = useState(null);
  const [dragging, setDragging] = useState(false);

  useEffect(() => {
    fetchBankOptionSets().then(setOptionSets);
  }, []);

  const labelMaps = useMemo(
    () => ({
      bankTypeLabelToValue: Object.fromEntries(
        (optionSets.bankType || []).map((o) => [o.label, o.value])
      ),
      countryLabelToValue: Object.fromEntries(
        (optionSets.country || []).map((o) => [o.label, o.value])
      ),
    }),
    [optionSets]
  );

  const handlePick = () => fileInputRef.current?.click();

  const handleDownloadTemplate = async () => {
    setDownloadingTemplate(true);
    setError(null);
    try {
      await downloadBulkUploadTemplate({
        bankTypeOptions: optionSets.bankType,
        countryOptions: optionSets.country,
      });
    } catch (err) {
      setError(err);
    } finally {
      setDownloadingTemplate(false);
    }
  };

  const acceptFile = async (selected) => {
    if (!selected) return;
    setFile(selected);
    setSummary(null);
    setImportResult(null);
    setError(null);
    setParsing(true);
    try {
      const buffer = await selected.arrayBuffer();
      const parsed = await parseBulkUploadWorkbook(buffer);
      setSummary(buildBulkUploadSummary(parsed, labelMaps));
    } catch (err) {
      setError(err);
    } finally {
      setParsing(false);
    }
  };

  const handleFileChange = (event) => acceptFile(event.target.files?.[0]);

  const handleDragOver = (event) => {
    event.preventDefault();
    setDragging(true);
  };
  const handleDragLeave = () => setDragging(false);
  const handleDrop = (event) => {
    event.preventDefault();
    setDragging(false);
    acceptFile(event.dataTransfer?.files?.[0]);
  };

  const validRows = (summary?.rows || []).filter((row) => row.firmUpsert);

  const handleImport = async () => {
    if (!validRows.length) return;
    setImporting(true);
    setError(null);
    try {
      const result = await api.bulkCreateFirms(
        validRows.map((row) => row.firmUpsert)
      );
      setImportResult(result);
    } catch (err) {
      setError(err);
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className={clsx(classes.formShell, classes.formShellAuto)}>
      <div className={classes.formTopBar}>
        <button
          type="button"
          aria-label="Back"
          title="Back"
          className={classes.formBackButton}
          onClick={onBack}
        >
          <ArrowBackIcon fontSize="small" />
        </button>
        <Typography component="h3" className={classes.formTitle}>
          Bulk Upload Companies
        </Typography>
        <div className={classes.formTopBarActions}>
          <A_Button
            label="Download Template"
            size="small"
            premiumVariant="secondary"
            startIcon={<GetAppIcon fontSize="small" />}
            onClick={handleDownloadTemplate}
            disabled={downloadingTemplate}
          />
        </div>
      </div>

      <div className={classes.formBody}>
        <input
          ref={fileInputRef}
          type="file"
          accept={`.xlsx,${XLSX_MIME}`}
          hidden
          onChange={handleFileChange}
        />
        <button
          type="button"
          className={clsx(
            classes.dropzone,
            file && classes.dropzoneFilled,
            dragging && classes.dropzoneDragging
          )}
          onClick={handlePick}
          onDragOver={handleDragOver}
          onDragEnter={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <DescriptionIcon className={classes.dropzoneIcon} />
          <span className={classes.dropzoneMainText}>
            {file ? (
              file.name
            ) : (
              <>
                Drag the filled-in template here, or{" "}
                <span className={classes.dropzoneBrowse}>browse</span>
              </>
            )}
          </span>
          <span className={classes.dropzoneHint}>
            {file ? "Click to choose a different file" : "Excel (.xlsx) only"}
          </span>
        </button>

        {parsing && <LoaderSkeleton />}

        <M_DataStateRenderer loading={false} error={error} data={summary}>
          {summary && (
            <>
              <div className={classes.sectionHeading}>Validation summary</div>
              <div className={classes.tableContainer}>
                <Table
                  size="small"
                  className={clsx(classes.table, classes.tableWide)}
                >
                  <TableHead className={classes.tableHead}>
                    <TableRow>
                      <TableCell>Row</TableCell>
                      <TableCell>Company Reference</TableCell>
                      <TableCell>Company</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Details</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody className={classes.tableBody}>
                    {summary.rows.map((row) => (
                      <TableRow
                        key={`${row.companyReference}-${row.rowNumber}`}
                      >
                        <TableCell>{row.rowNumber}</TableCell>
                        <TableCell>{row.companyReference}</TableCell>
                        <TableCell className={classes.cellStrong}>
                          {row.companyName}
                        </TableCell>
                        <TableCell>
                          <StatusTag
                            tone={STATUS_TONE[row.status] || "neutral"}
                          >
                            {row.status}
                          </StatusTag>
                        </TableCell>
                        <TableCell>
                          {row.status === "Ready" ? (
                            row.message
                          ) : (
                            <ul className={classes.errorList}>
                              {row.fieldErrors.map((fe, index) => (
                                <li key={index}>{fe.message}</li>
                              ))}
                            </ul>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <div className={classes.summaryBar}>
                {summary.readyCount} ready · {summary.warningCount} warning ·{" "}
                {summary.errorCount} error - of {summary.totalRows} compan
                {summary.totalRows === 1 ? "y" : "ies"}.
              </div>

              {!importResult && (
                <div className={classes.formActionBar}>
                  <A_SecuredAction permission={pageSecurityActions.CREATE}>
                    <A_Button
                      label={`Import ${validRows.length} Valid Compan${
                        validRows.length === 1 ? "y" : "ies"
                      }`}
                      size="small"
                      premiumVariant="primary"
                      startIcon={<UploadIcon fontSize="small" />}
                      onClick={handleImport}
                      disabled={importing || !validRows.length}
                    />
                  </A_SecuredAction>
                </div>
              )}
            </>
          )}
        </M_DataStateRenderer>

        {importing && <LoaderSkeleton />}

        <M_DataStateRenderer loading={false} error={null} data={importResult}>
          {importResult && (
            <>
              <div className={classes.sectionHeading}>Import result</div>
              <div className={classes.tableContainer}>
                <Table
                  size="small"
                  className={clsx(classes.table, classes.tableWide)}
                >
                  <TableHead className={classes.tableHead}>
                    <TableRow>
                      <TableCell>Row</TableCell>
                      <TableCell>Company</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Message</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody className={classes.tableBody}>
                    {(importResult.rows || []).map((row) => (
                      <TableRow key={row.rowNumber}>
                        <TableCell>{row.rowNumber}</TableCell>
                        <TableCell className={classes.cellStrong}>
                          {row.companyName}
                        </TableCell>
                        <TableCell>
                          <StatusTag
                            tone={STATUS_TONE[row.status] || "neutral"}
                          >
                            {row.status}
                          </StatusTag>
                        </TableCell>
                        <TableCell>{row.message}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <div className={classes.summaryBar}>
                {importResult.readyCount} ready · {importResult.warningCount}{" "}
                warning · {importResult.errorCount} error - of{" "}
                {importResult.totalRows} row
                {importResult.totalRows === 1 ? "" : "s"} sent.
              </div>
            </>
          )}
        </M_DataStateRenderer>
      </div>
    </div>
  );
};

BulkUpload.propTypes = {
  onBack: PropTypes.func.isRequired,
};

export default BulkUpload;
